import type { LucideIcon } from 'lucide-react';
import {
  Droplets,
  Lamp,
  LampDesk,
  Lightbulb,
  Monitor,
  Moon,
  Snowflake,
  Sparkles,
  Square,
  Sun,
  Sunset,
  Tv,
  Wind,
} from 'lucide-react';

/**
 * App-wide device vocabulary. This is the only module allowed to spell these
 * values out — everything else references the constants below, which keeps a
 * rename to one line and makes every switch over them exhaustively checked.
 */

/** Power state of anything that can be switched. */
export const POWER_STATE = {
  ON: 'on',
  OFF: 'off',
} as const;

export type PowerState = (typeof POWER_STATE)[keyof typeof POWER_STATE];

/** Air-conditioner modes. `next` owns the cycle order used by the mode button. */
export const AC_MODE = {
  COOL: 'cool',
  HEAT: 'heat',
  FAN: 'fan',
  DRY: 'dry',
} as const;

export type AcMode = (typeof AC_MODE)[keyof typeof AC_MODE];

export interface AcModeSpec {
  readonly label: string;
  readonly icon: LucideIcon;
  readonly next: AcMode;
}

export const AC_MODE_SPECS = {
  [AC_MODE.COOL]: { label: 'Cool', icon: Snowflake, next: AC_MODE.HEAT },
  [AC_MODE.HEAT]: { label: 'Heat', icon: Sun, next: AC_MODE.FAN },
  [AC_MODE.FAN]: { label: 'Fan', icon: Wind, next: AC_MODE.DRY },
  [AC_MODE.DRY]: { label: 'Dry', icon: Droplets, next: AC_MODE.COOL },
} as const satisfies Record<AcMode, AcModeSpec>;

/** Kinds of device the room controls page knows how to render. */
export const DEVICE_KIND = {
  LIGHT: 'light',
  DIMMABLE_LIGHT: 'dimmable-light',
  CURTAIN: 'curtain',
  AC: 'ac',
  TV: 'tv',
  FAN: 'fan',
} as const;

export type DeviceKind = (typeof DEVICE_KIND)[keyof typeof DEVICE_KIND];

/** Where in the room a device lives — used for grouping on the controls page. */
export const DEVICE_CATEGORY = {
  BEDROOM: 'bedroom',
  BATHROOM: 'bathroom',
  LIVING: 'living',
  PASSAGE: 'passage',
} as const;

export type DeviceCategory = (typeof DEVICE_CATEGORY)[keyof typeof DEVICE_CATEGORY];

/** Device artwork. A key is stored on the device; the map supplies the glyph. */
export const DEVICE_ICON = {
  LAMP_DESK: 'LampDesk',
  LAMP: 'Lamp',
  LIGHTBULB: 'Lightbulb',
  TV: 'Tv',
  MONITOR: 'Monitor',
  SNOWFLAKE: 'Snowflake',
  CURTAIN: 'Square',
  SUN: 'Sun',
  MOON: 'Moon',
  WIND: 'Wind',
  DROPLETS: 'Droplets',
} as const;

export type DeviceIconKey = (typeof DEVICE_ICON)[keyof typeof DEVICE_ICON];

export const DEVICE_ICON_MAP = {
  [DEVICE_ICON.LAMP_DESK]: LampDesk,
  [DEVICE_ICON.LAMP]: Lamp,
  [DEVICE_ICON.LIGHTBULB]: Lightbulb,
  [DEVICE_ICON.TV]: Tv,
  [DEVICE_ICON.MONITOR]: Monitor,
  [DEVICE_ICON.SNOWFLAKE]: Snowflake,
  [DEVICE_ICON.CURTAIN]: Square,
  [DEVICE_ICON.SUN]: Sun,
  [DEVICE_ICON.MOON]: Moon,
  [DEVICE_ICON.WIND]: Wind,
  [DEVICE_ICON.DROPLETS]: Droplets,
} as const satisfies Record<DeviceIconKey, LucideIcon>;

/** Mood preset artwork. */
export const MOOD_ICON = {
  SUN: 'Sun',
  SUNSET: 'Sunset',
  SPARKLES: 'Sparkles',
  MOON: 'Moon',
} as const;

export type MoodIconKey = (typeof MOOD_ICON)[keyof typeof MOOD_ICON];

export const MOOD_ICON_MAP = {
  [MOOD_ICON.SUN]: Sun,
  [MOOD_ICON.SUNSET]: Sunset,
  [MOOD_ICON.SPARKLES]: Sparkles,
  [MOOD_ICON.MOON]: Moon,
} as const satisfies Record<MoodIconKey, LucideIcon>;

/** Percentage scale shared by dimmer levels and curtain positions. */
export const PERCENT = {
  MIN: 0,
  MAX: 100,
  /** Level assumed when a device has never reported one. */
  DEFAULT: 50,
} as const;

/** Comfort bounds for the in-room thermostat, in Celsius. */
export const TEMPERATURE_C = {
  MIN: 16,
  MAX: 30,
  STEP: 1,
  /** Fallbacks for devices that have never reported a reading. */
  DEFAULT_TARGET: 22,
  DEFAULT_CURRENT: 24,
} as const;

/** Milliseconds of drag to coalesce before a slider commits a value. */
export const SLIDER_COMMIT_DELAY_MS = 150;
